import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Tabs, 
  Tab, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  useMediaQuery, 
  useTheme,
  Button,
  Box,
} from '@mui/material';
import { Menu as MenuIcon, Logout, AccountCircle } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const { user, logout, isAuthenticated } = useAuth();

  const pages = [
    { label: t('navigation.appointments'), path: '/appointments' },
    { label: t('navigation.calendar'), path: '/calendar' },
    { label: t('navigation.clients'), path: '/clients' },
    { label: t('navigation.services'), path: '/services' },
    { label: t('navigation.products'), path: '/products' },
    { label: t('navigation.sales'), path: '/sales' },
    { label: t('navigation.timeoffs'), path: '/timeoffs' },
    { label: t('navigation.statistics'), path: '/statistics' },
  ];

  const currentTab = pages.findIndex(page => page.path === location.pathname);
  const isLoginPage = location.pathname === '/login';
  
  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(pages[newValue].path);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleMobileMenuSelect = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleUserMenuClose();
  };

  return (
    <AppBar position="static">
      <Toolbar>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('navigation.hairSalonCrm')}
        </Typography>
        
        {!isLoginPage && isAuthenticated && (
          <>
            {isMobile ? (
              <>
                <IconButton
                  color="inherit"
                  aria-label="open menu"
                  onClick={handleMobileMenuOpen}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={mobileMenuAnchor}
                  open={Boolean(mobileMenuAnchor)}
                  onClose={handleMobileMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  {pages.map((page) => (
                    <MenuItem
                      key={page.path}
                      onClick={() => handleMobileMenuSelect(page.path)}
                      selected={location.pathname === page.path}
                    >
                      {page.label}
                    </MenuItem>
                  ))}
                  <MenuItem onClick={handleLogout}>
                    <Logout sx={{ mr: 1 }} />
                    {t('navigation.logout')}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Tabs
                  value={currentTab === -1 ? false : currentTab}
                  onChange={handleChange}
                  textColor="inherit"
                  indicatorColor="secondary"
                >
                  {pages.map((page) => (
                    <Tab key={page.path} label={page.label} />
                  ))}
                </Tabs>
                <Box sx={{ ml: 2 }}>
                  <Button
                    color="inherit"
                    startIcon={<AccountCircle />}
                    onClick={handleUserMenuOpen}
                    sx={{ textTransform: 'none' }}
                  >
                    {user?.firstName} {user?.lastName}
                  </Button>
                  <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <MenuItem onClick={handleLogout}>
                      <Logout sx={{ mr: 1 }} />
                      {t('navigation.logout')}
                    </MenuItem>
                  </Menu>
                </Box>
              </>
            )}
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
